import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/ui/Button'
import { Sheet } from '@/components/ui/Sheet'
import { StatusPill } from '@/components/ui/StatusPill'
import { refusalMessage } from '@/lib/refusals'
import { useSession } from '@/features/auth/api/useSession'
import { useIssueDocument, useRenderDocument } from '@/features/finance/api/useFinance'
import { useTrancheCarryOver } from '@/features/finance/api/useReceivables'
import { receivableLabel } from '@/features/finance/allocationLabel'
import { formatMoneyString } from '@/lib/format'
import { mayIssueDocument } from '@/features/finance/documentAccess'
import { documentStatusOf, type CaseDocument, type DocumentStatus } from '@/types/domain'

/* What a reader may DO to one piece of paper, and what the paper's pill says —
 * in one file, because both surfaces that list documents need both.
 *
 * ── Why this is extracted rather than written twice ────────────────────────
 *
 * There are two Documents surfaces: the case's own section and the Money page's
 * tab. They are two VIEWS of one archive (`00-decisions.md` §4), and the whole
 * value of that sentence is that a document cannot be in one state here and
 * another state there. The state machine below — generate, then issue, and
 * never issue what has not been generated — is the part most easily copied
 * slightly wrong: the copy that forgets `documentId` offers Issue on a draft
 * nobody rendered, earns a refusal the server was always going to make, and
 * teaches the reader that the button is unreliable rather than that the rule
 * exists. So it lives once, and both screens mount it.
 *
 * NOTE FOR THE NEXT CHANGE: at the time this file was written the case's
 * section (`features/cases/components/CaseDocuments.tsx`) still carried its own
 * copy of the tone table and its own action strip. Switching it over to these
 * two exports is the remaining half of the extraction — it was not done in the
 * same pass only because that file was being rewritten concurrently, not
 * because the two screens are meant to differ.
 *
 * ── The three stages are three server calls (00-decisions §1) ──────────────
 *
 *   Generate  renders the PDF into the archive. Repeatable, costs nothing but
 *             a render, moves no ledger figure.
 *   Issue     mints the number, freezes the document, creates the receivable.
 *             One-way (AD-24), so it is CONFIRMED before it is taken.
 *
 * There is deliberately no third button that does both: "issue on generate" is
 * a flag, and a flag is something a client sets by accident (UX-DR-15, and
 * `document.Handlers.Issue`'s own comment).
 */

/** The pill's colour per derived state.
 *
 *  FOUR entries, and the union has four members — there is no `void`, because
 *  nothing in this system produces one (`00-decisions.md` §3, AD-24). A fifth
 *  row here would be a tone waiting for a state that cannot arrive.
 *
 *  `part-paid` is ATTENTION rather than positive: money has arrived and the
 *  document is still owed on, which is the row a person chasing receivables
 *  wants to find, not the row they want reassured about. */
const DOCUMENT_STATUS_TONES: Record<
  DocumentStatus,
  'neutral' | 'brand' | 'positive' | 'attention' | 'negative'
> = {
  draft: 'neutral',
  issued: 'brand',
  'part-paid': 'attention',
  paid: 'positive',
}

/** Where a document stands, as a pill.
 *
 *  Takes the RECORD rather than a status, so the derivation happens in exactly
 *  one place: `documentStatusOf` reads the served figures and this renders what
 *  it answers. A prop typed `DocumentStatus` would let a caller derive its own
 *  and hand it in, which is how one screen comes to call a document part-paid
 *  while the other still calls it issued — the disagreement a reader resolves
 *  by distrusting both screens. */
export function DocumentStatusPill({ record }: { record: CaseDocument }) {
  const { t } = useTranslation()
  const status = documentStatusOf(record)
  return (
    <StatusPill tone={DOCUMENT_STATUS_TONES[status]}>
      {t(`docStatus.${status}`)}
    </StatusPill>
  )
}

/** Whether a CURRENT render exists, said in words.
 *
 *  Said rather than left blank, because a blank cell in an archive reads as a
 *  column that failed to load rather than as a document nobody has generated
 *  yet — and the difference decides whether the reader presses Generate or
 *  reloads the page. `documentId` is the whole signal: the server clears it on
 *  every draft write, so its absence means "no current render" rather than "was
 *  never rendered", and one word covers both. */
export function DocumentRenderState({ record }: { record: CaseDocument }) {
  const { t } = useTranslation()
  return record.documentId === undefined ? (
    <span className="text-text-muted text-[12.5px] font-light">
      {t('finance.documents.notRendered')}
    </span>
  ) : (
    <span className="text-text-body text-[12.5px]">
      {t('finance.documents.rendered')}
    </span>
  )
}

/** Generate, Issue, and — when the caller has a sheet to open — Edit.
 *
 *  Nothing here is optimistic (UX-DR-24). Both mutations answer with the whole
 *  record and invalidate the case's document reads, so what the strip renders
 *  next is what the server confirmed; a button that flipped its own label ahead
 *  of the response would be this screen asserting that a render succeeded
 *  before any bytes existed.
 *
 *  Both refusals are BLOCKED rather than disabled, which is the house rule and
 *  is load-bearing here: the two reasons a person cannot issue — nothing has
 *  been rendered, or it has already gone out — are exactly the two sentences
 *  they need in order to know what to do next, and a `disabled` button cannot
 *  be hovered, cannot be focused and is skipped by a screen reader. */
export function DocumentActions({
  record,
  /** Opens whatever editor the calling surface has for this kind. Absent when
   *  the surface offers none — the strip then simply has no Edit button, which
   *  is also what an ISSUED record gets regardless of this prop. */
  onEdit,
  /** The tranche an invoice replaces, when it names one — the issue then
   *  carries the money already received on it over to the invoice (S-21), and
   *  the confirmation says how much. */
  trancheId,
}: {
  record: CaseDocument
  onEdit?: () => void
  trancheId?: string | null
}) {
  const { t } = useTranslation()
  const { data: user } = useSession()
  const render = useRenderDocument()
  const issue = useIssueDocument()

  /* Issuing is its own capability, per kind (`mayIssueDocument`). A reader
     who may draft but not issue gets no Issue button at all — absent, not
     refused: no state of the paper would ever let them press it, so a
     `blockedReason` here would be a sentence about the reader dressed up as
     one about the document. */
  const mayIssue = mayIssueDocument(user, record.kind)

  /* The confirmation is a piece of local state rather than a prop, because it
     is about the PRESS and not about the record: it opens on the button and
     closes on either outcome, and a parent holding it would have to be told
     about a decision that never leaves this strip. */
  const [confirming, setConfirming] = useState(false)

  const carried = useTrancheCarryOver(record.caseId, trancheId, {
    enabled: confirming && record.kind === 'invoice' && !record.issued,
  })

  /* The two mutations take the same handle. The kind travels ON it rather than
     being inferred from the id server-side, because nothing about a uuid says
     which of the two tables it came from — see `DocumentRef`. */
  const ref = { caseId: record.caseId, kind: record.kind, id: record.id }

  const rendered = record.documentId !== undefined

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center gap-2">
        {/* A second press REPLACES a file that is already in the archive, and
            the label says so. "Generate PDF" on a record that has one would
            read as a first render, and the reader would not know whether the
            thing they are about to send is the one they just looked at. */}
        <Button
          variant="secondary"
          size="sm"
          onClick={() => render.mutate(ref)}
          // `disabled`, not `blockedReason`: the cause is already on screen —
          // the label itself has changed to "Generating…".
          disabled={render.isPending}
        >
          {render.isPending
            ? t('finance.documents.actions.generating')
            : rendered
              ? t('finance.documents.actions.regenerate')
              : t('finance.documents.actions.generate')}
        </Button>

        {/* Offered on an issued document too, and refused with a sentence
            rather than removed. Somebody who presses Issue twice has to be
            told the FIRST press worked; a button that quietly disappeared
            would leave them wondering whether it ever did anything, and a
            swallowed refusal teaches the opposite of what happened. */}
        {mayIssue ? (
          <Button
            size="sm"
            onClick={() => setConfirming(true)}
            disabled={issue.isPending}
            blockedReason={
              record.issued
                ? t('finance.documents.alreadyIssued')
                : rendered
                  ? undefined
                  : t('finance.documents.needsRender')
            }
          >
            {issue.isPending
              ? t('finance.documents.actions.issuing')
              : t('finance.documents.actions.issue')}
          </Button>
        ) : null}

        {/* AC 9's affordance, and it is an affordance rather than a guard: the
            server refuses the edit regardless. An issued document loses the
            button and gains the sentence below, because presenting fields that
            would be rejected is worse than presenting none. */}
        {onEdit !== undefined && !record.issued ? (
          <Button variant="ghost" size="sm" onClick={onEdit}>
            {t('common.actions.edit')}
          </Button>
        ) : null}
      </div>

      {record.issued ? (
        <p className="text-text-muted text-[12.5px] leading-snug font-light">
          {t('finance.documents.issuedReadOnly')}
        </p>
      ) : null}

      {/* Our sentence, never the server's `detail` — that is English written
          for a log (AD-22, AD-27). `refusalMessage` also names the fields of an
          issuing entity with no legal footer or bank details (FR-64), which is
          the refusal a render earns most often and the one the generic wording
          cannot act on: it points at highlighted fields, and there is no form
          here to highlight. */}
      {render.isError ? (
        <p role="alert" className="text-negative text-[12.5px] leading-snug">
          {refusalMessage(render.error, t)}
        </p>
      ) : null}
      {/* `&& !confirming` so the refusal is said ONCE. The confirmation stays
          open when the issue is refused — it closes from `onSuccess` alone —
          and it carries the same sentence inside itself, where the reader
          whose attention is on the dialog can actually see it. Rendering both
          would put two alerts on one failure, one of them behind a scrim. */}
      {issue.isError && !confirming ? (
        <p role="alert" className="text-negative text-[12.5px] leading-snug">
          {refusalMessage(issue.error, t)}
        </p>
      ) : null}

      {/* Issuing is the irreversible one, so it asks — the same shape the rate
          approval uses, and for the same reason: AD-24 makes this one-way, so
          there is no undo to offer afterwards. The body names all three
          consequences and then names the remedy, because "cannot be undone" on
          its own reads as a dead end rather than as the ordinary way paper is
          corrected. */}
      {confirming ? (
        <Sheet
          open
          onClose={() => setConfirming(false)}
          title={t('finance.documents.issueConfirm.title')}
          footer={
            <>
              <Button
                className="w-full"
                disabled={issue.isPending}
                onClick={() =>
                  issue.mutate(ref, {
                    // Closed from `onSuccess` only. A refusal keeps the
                    // confirmation on screen with its message beneath it,
                    // rather than closing over a write that never happened.
                    onSuccess: () => setConfirming(false),
                  })
                }
              >
                {issue.isPending
                  ? t('finance.documents.actions.issuing')
                  : t('finance.documents.issueConfirm.confirm')}
              </Button>
              <Button
                className="w-full"
                variant="secondary"
                onClick={() => setConfirming(false)}
              >
                {t('common.actions.cancel')}
              </Button>
            </>
          }
        >
          <p className="text-text-body text-[13px] leading-relaxed">
            {t('finance.documents.issueConfirm.body')}
          </p>
          {carried ? (
            <p className="text-text-body text-[13px] leading-relaxed">
              {t('finance.receivables.carryOver', {
                tranche: receivableLabel(t, carried),
                amount: formatMoneyString(carried.paid.amount, carried.paid.currency),
              })}
            </p>
          ) : null}
          {issue.isError ? (
            <p role="alert" className="text-negative text-[12.5px] leading-snug">
              {refusalMessage(issue.error, t)}
            </p>
          ) : null}
        </Sheet>
      ) : null}
    </div>
  )
}
